import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PlusCircle } from "feather-icons-react/build/IconComponents";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { ErrorToaster } from "../components/toaster/Toaster";
import { generatePdf } from "../../utils/helper";
import dayjs from "dayjs";
import { Table } from "antd";
import ReportServices from "../../services/Report";
import { all_routes } from "../../Router/all_routes";
import { useSelector } from "react-redux";

const RefundReceipt = () => {
  const invoiceRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useSelector((state) => ({
    userData: state.user_data
  }));

  const storeLogo = userData?.store?.logoBase64 || '/assets/img/mattress-plus-logo.png';
  const storeQrCode = userData?.store?.qrCodeBase64 || '/assets/img/store/store-qr.png';

  // *For Loading
  const [loading, setLoading] = useState(false);

  // *For Refund Receipt Data
  const [receiptData, setReceiptData] = useState(null);

  // *For Generate PDF
  const handleGeneratePdf = async () => {
    setLoading(true);
    try {
      await generatePdf([invoiceRef], 'refund-receipt');
    } catch (error) {
      console.log('handleGeneratePdf => error:', error);
    } finally {
      setLoading(false);
    }
  };

  // *For Get Refund Receipt Details
  const getRefundReceiptDetails = async () => {
    try {
      setLoading(true);
      const response = await ReportServices.generateRefundReceipt(id);
      setReceiptData(response.data || response);
    } catch (error) {
      console.log('Error fetching refund receipt details:', error);
      ErrorToaster(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getRefundReceiptDetails();
    }
  }, [id]);

  // --- Columns for refunded items ---
  const refundedItemsColumns = [
    {
      title: "Item Name",
      dataIndex: "name",
      render: (text, record) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.variantDetails && (
            <small className="text-muted">{record.variantDetails}</small>
          )}
        </div>
      ),
    },
    {
      title: "Sale Price",
      dataIndex: "salePrice",
      render: (text) => (
        <span>${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Tax",
      dataIndex: "taxAmount",
      render: (text) => (
        <span>${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      render: (text) => (
        <span>{text}</span>
      ),
    },
    {
      title: "Fee Deduction",
      dataIndex: "feeDeduction",
      render: (text) => (
        <span className="text-danger">-${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Pickup Charge",
      dataIndex: "pickupCharge",
      render: (text) => (
        <span className="text-danger">-${parseFloat(text || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (text) => (
        <span className="text-muted small">{text || 'N/A'}</span>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header d-flex align-items-center justify-content-between">
          <div className="page-title">
            <h4>View Refund Receipt</h4>
            <small className="text-danger d-block">
              <i className="fas fa-undo-alt me-1"></i>
              Refund Receipt
            </small>
          </div>
          <div className="d-flex align-items-center">
            <button
              className="btn btn-outline-primary me-2"
              onClick={() => navigate('/sales')}
            >
              <i className="fas fa-arrow-left me-1"></i>
              Back to Sales
            </button>
            <button disabled={loading} className="btn btn-added" onClick={() => handleGeneratePdf()}>
              <PlusCircle className="me-2 iconsize" />
              Print Receipt
            </button>
          </div>
        </div>

        <div className="card-body mb-3">
          <div id='receiptToPrint' className="card m-auto" style={{ width: '950px' }}>
            <div ref={invoiceRef}>
              <div className="card-body add-product p-4">
                <div className="row justify-content-between mb-1">
                  <div className="col-md-4">
                    <img src={storeLogo} alt="img" style={{ height: 'auto', width: 'auto', objectFit: 'contain' }} />
                    <p className="mb-1">{userData?.store?.address.split(',')[0]}</p>
                    <p className="mb-1">{userData?.store?.address.split(', ').splice(1).join(', ')}</p>
                    <p className="mb-1">{userData?.store?.phone_number}</p>
                  </div>
                  <div className="col-md-2">
                    <img src={storeQrCode} alt="img" style={{ height: 'auto', width: 'auto', objectFit: 'contain' }} />
                  </div>
                  <div className="col-md-5">
                    <h3 className="mb-1">REFUND RECEIPT</h3>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Receipt ID:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.receiptId}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Original Invoice:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.originalInvoice?.orderNumber}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Refund Date:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{dayjs(receiptData?.refundDate).format('MM-DD-YYYY')}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Status:</p>
                      </div>
                      <div className="col-md-8">
                        <span className={`badge ${receiptData?.status === 'processed' ? 'bg-success' : 'bg-warning'}`}>
                          {receiptData?.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row justify-content-between">
                  <div className="col-md-5">
                    <h6 className="text-center py-1" style={{ backgroundColor: 'rgb(220, 53, 69)', color: '#fff' }}>Customer Info</h6>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Name:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.customer?.name}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Phone:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.customer?.phone}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Email:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.customer?.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <h6 className="text-center py-1" style={{ backgroundColor: 'rgb(220, 53, 69)', color: '#fff' }}>Refund Details</h6>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Method:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.method}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Tax Rate:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.summary?.taxRate}%</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <p>Reason:</p>
                      </div>
                      <div className="col-md-8">
                        <p>{receiptData?.reason}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Refunded Items Section */}
                <div className="mb-4">
                  <h5 className="mb-3" style={{ color: '#dc3545', borderBottom: '2px solid #dc3545', paddingBottom: '5px' }}>
                    <i className="fas fa-undo-alt me-2"></i>
                    Refunded Items
                  </h5>
                  <Table
                    className="table datanew dataTable no-footer custom-table"
                    columns={refundedItemsColumns}
                    dataSource={receiptData?.refundedItems || []}
                    pagination={false}
                    size="small"
                    scroll={{ x: 800 }}
                  />
                </div>

                {/* Refund Summary Section */}
                <div className="mb-4">
                  <h5 className="mb-3" style={{ color: '#dc3545', borderBottom: '2px solid #dc3545', paddingBottom: '5px' }}>
                    <i className="fas fa-calculator me-2"></i>
                    Refund Calculation Summary
                  </h5>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card" style={{ border: '1px solid #e9ecef' }}>
                        <div className="card-body p-3">
                          <h6 className="card-title" style={{ color: '#495057' }}>Items Total Value</h6>
                          <p className="card-text fs-5 fw-bold text-primary">
                            ${parseFloat(receiptData?.summary?.itemsTotal || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card" style={{ border: '1px solid #e9ecef' }}>
                        <div className="card-body p-3">
                          <h6 className="card-title" style={{ color: '#495057' }}>Total Tax Amount</h6>
                          <p className="card-text fs-5 fw-bold text-info">
                            ${parseFloat(receiptData?.summary?.taxAmount || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col-md-12">
                      <div className="card" style={{ border: '1px solid #dc3545' }}>
                        <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                          <h6 className="mb-0">Financial Breakdown</h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-2">
                                <span className="text-muted">Processing Fees (20%):</span>
                                <span className="float-end fw-bold text-danger">
                                  -${parseFloat(receiptData?.summary?.totalFees || 0).toFixed(2)}
                                </span>
                              </div>
                              <div className="mb-2">
                                <span className="text-muted">Pickup Charges:</span>
                                <span className="float-end fw-bold text-danger">
                                  -${parseFloat(receiptData?.summary?.pickupCharges || 0).toFixed(2)}
                                </span>
                              </div>
                              <div className="mb-2">
                                <span className="text-muted">Subtotal (after deductions):</span>
                                <span className="float-end fw-bold">
                                  ${parseFloat(receiptData?.summary?.subtotal || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-2">
                                <span className="text-muted">Tax Amount ({receiptData?.summary?.taxRate}%):</span>
                                <span className="float-end fw-bold">
                                  +${parseFloat(receiptData?.summary?.taxAmount || 0).toFixed(2)}
                                </span>
                              </div>
                              <div className="border-top pt-2">
                                <span className="text-danger fw-bold">Net Refund Amount:</span>
                                <span className="float-end fs-5 fw-bold text-danger">
                                  ${parseFloat(receiptData?.summary?.netRefund || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mt-4">
                  <div className="col-lg-12">
                    <div className="total-order w-100 max-widthauto m-auto text-center">
                      <p className="mb-2">Thank you for your business. Refund has been processed successfully.</p>
                      <small className="text-muted">This receipt serves as proof of refund transaction.</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundReceipt;